import { Popover, PopoverAnchor } from '@heidi/design-system/popover';
import { useDirection } from '@radix-ui/react-direction';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RecentVisitHoverCard } from './recent-visit-hover-card';
import {
  RecentVisitHoverCardContext,
  type RecentVisitHoverCardController,
  type RecentVisitHoverTarget,
} from './recent-visit-hover-card-context';

/** Long enough that a pointer crossing the list on its way somewhere else does
 *  not trail a card behind it, short enough to read as a preview. It applies
 *  ONLY to a cold start — once the card is up, the next row is instant. */
const OPEN_DELAY_MS = 400;
/** Covers the pointer crossing from a row into the card, or between two rows. */
const CLOSE_DELAY_MS = 120;

/**
 * The nav panel the card sits beside, most specific first. The card is placed
 * against this element's INLINE-END edge rather than the row's, so it clears
 * the navigation instead of starting inside it — a row is inset by the panel's
 * own padding, and anchoring to the row put the card's leading edge back over
 * the nav.
 *
 * The fallback keeps a card anchored to its row if the panel is ever
 * restructured, which is a slightly wrong position rather than none at all.
 */
const NAV_PANEL_SELECTORS = ['#agent-home-panel', '[data-slot="sidebar-inner"]'];

/** A zero-width rect: the nav panel's inline-end edge, level with the row. */
interface CardAnchor {
  getBoundingClientRect: () => DOMRect;
  /** Lets floating-ui watch the row's scroll ancestors, which a virtual
   *  reference has no way to find on its own. */
  contextElement?: Element | null;
}

/**
 * One hover card for the whole Recents list.
 *
 * Shared rather than per-row because of what moving down the list should feel
 * like. With a card per row, leaving row A closes A and entering row B opens B,
 * so dragging the pointer down a list plays a collapse-and-expand at every
 * step. One card that stays mounted and swaps its props instead just changes
 * what it says, which is what a preview should do.
 *
 * That also decides the timing, which no single row could own: the open delay
 * is a property of the LIST, spent once on the way in. While the card is up the
 * next row is instant; leave the list for longer than the close delay and the
 * next entry pays the delay again.
 *
 * Mounted above every row (`RecentsSectionBody`) because rows are split across
 * date groups and a shared card cannot live inside one of them. It adds no DOM
 * of its own: the Popover root renders nothing, the anchor is virtual, and the
 * content is portaled.
 */
export const RecentVisitHoverCardHost = ({ children }: { children: ReactNode }) => {
  // The nav sits on the inline start, so the card opens toward the content
  // column — which is the screen's left in an RTL locale. Radix's `side` is
  // physical and does not flip on its own, and `DOMRect.right` is a physical
  // edge, so both have to be resolved from direction or an RTL card opens off
  // the outer edge of the window.
  const isRtl = useDirection() === 'rtl';
  const [active, setActive] = useState<{
    target: RecentVisitHoverTarget;
    row: HTMLLIElement;
  } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const cancelClose = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, []);

  const close = useCallback(() => {
    cancelClose();
    setIsOpen(false);
  }, [cancelClose]);

  const requestOpen = useCallback<RecentVisitHoverCardController['requestOpen']>(
    (target, row, options) => {
      cancelClose();
      setActive({ target, row });
      if (isOpen) return;
      if (options?.immediate) {
        setIsOpen(true);
        return;
      }
      // Restarted per row, so the card opens where the pointer RESTED rather
      // than wherever it happens to be when a timer started three rows ago.
      timerRef.current = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    },
    [cancelClose, isOpen]
  );

  const requestClose = useCallback(() => {
    cancelClose();
    timerRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  const focusFirstAction = useCallback(() => {
    const first = contentRef.current?.querySelector<HTMLElement>('button');
    if (!first) return false;
    first.focus();
    return true;
  }, []);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const activeRow = active?.row ?? null;
  // Rebuilt per row so its identity changes, which is the only signal Radix's
  // popper takes to re-anchor — a virtual reference that quietly returns a new
  // rect leaves floating-ui measuring, and observing, the row it started on.
  const anchor = useMemo<CardAnchor>(
    () => ({
      contextElement: activeRow,
      getBoundingClientRect: () => {
        if (!activeRow) return new DOMRect();
        const rowRect = activeRow.getBoundingClientRect();
        const panel = NAV_PANEL_SELECTORS.reduce<Element | null>(
          (found, selector) => found ?? activeRow.closest(selector),
          null
        );
        const panelRect = panel?.getBoundingClientRect();
        const edge = isRtl
          ? (panelRect?.left ?? rowRect.left)
          : (panelRect?.right ?? rowRect.right);
        return new DOMRect(edge, rowRect.top, 0, rowRect.height);
      },
    }),
    [activeRow, isRtl]
  );
  const anchorRef = useRef<CardAnchor>(anchor);
  // Written during render rather than in an effect: `PopoverAnchor` is a child,
  // so its effect reads this ref before a parent effect could have set it, and
  // the card would spend a frame pointing at the previous row.
  anchorRef.current = anchor;

  const returnFocusToActiveRow = useCallback(() => {
    activeRow?.querySelector<HTMLElement>('button, a')?.focus();
  }, [activeRow]);

  const controller = useMemo<RecentVisitHoverCardController>(
    () => ({
      activeSessionId: active?.target.session.session_id ?? null,
      isOpen,
      requestOpen,
      requestClose,
      cancelClose,
      close,
      contentRef,
      focusFirstAction,
    }),
    [
      active?.target.session.session_id,
      isOpen,
      requestOpen,
      requestClose,
      cancelClose,
      close,
      focusFirstAction,
    ]
  );

  return (
    <RecentVisitHoverCardContext.Provider value={controller}>
      {children}
      <Popover open={isOpen && !!active} onOpenChange={(next) => !next && close()}>
        <PopoverAnchor virtualRef={anchorRef} />
        {active ? (
          <RecentVisitHoverCard
            ref={contentRef}
            session={active.target.session}
            patientName={active.target.patientName}
            sessionLabel={active.target.sessionLabel}
            onClose={close}
            onReturnFocus={returnFocusToActiveRow}
            side={isRtl ? 'left' : 'right'}
            onMouseEnter={cancelClose}
            onMouseLeave={requestClose}
          />
        ) : null}
      </Popover>
    </RecentVisitHoverCardContext.Provider>
  );
};

/** No-op implementation of every `RecentVisitHoverCardController` member —
 *  cheap and stable across renders, since nothing here ever changes. */
const INERT_RECENT_VISIT_HOVER_CARD_CONTROLLER: RecentVisitHoverCardController = {
  activeSessionId: null,
  isOpen: false,
  requestOpen: () => {},
  requestClose: () => {},
  cancelClose: () => {},
  close: () => {},
  contentRef: { current: null },
  focusFirstAction: () => false,
};

/**
 * Turns the nested encounter hover card OFF for a list of rows: same
 * `RecentVisitHoverCardContext`, an inert controller. `RecentVisitRow` calls
 * `useRecentVisitHoverCardController`, which throws outside a provider, so
 * rows inside the Home hover preview still need *a* provider — just one that
 * never opens a card, so nothing portaled can land over the preview's list
 * and pull the pointer out of it (see the Home hover preview spec's "Nested
 * encounter hover card" decision). Rows still render, hover, and navigate
 * exactly as before; only the second, portaled card is gone.
 */
export const InertRecentVisitHoverCardHost = ({ children }: { children: ReactNode }) => (
  <RecentVisitHoverCardContext.Provider value={INERT_RECENT_VISIT_HOVER_CARD_CONTROLLER}>
    {children}
  </RecentVisitHoverCardContext.Provider>
);
